<template>
    <div class="flex flex-col items-center">
        <div class="w-full">
            <div
                class="bg-theme-card w-full border border-theme-border rounded-xl p-5"
            >
                <label class="text-sm text-theme-muted block mb-2">
                    Nombre de tu negocio
                </label>
                <input
                    v-model="tenantName"
                    placeholder="Ej: Mi Negocio, Juan Pérez"
                    maxlength="120"
                    :disabled="readOnly"
                    class="outline-none text-lg w-full bg-transparent text-theme-base disabled:text-theme-disabled"
                    @blur="nameTouched = true"
                    @input="onNameInput"
                    @keyup.enter="continueStep"
                />
            </div>
            <p
                v-if="nameTouched && !isValidName"
                class="text-theme-danger text-sm mt-1"
            >
                El nombre debe tener entre 3 y 120 caracteres
            </p>
        </div>

        <div
            v-if="slugBase"
            class="mt-6 w-full bg-theme-card border border-theme-border rounded-xl p-5"
        >
            <label class="text-sm text-theme-muted block mb-2">
                Tu catálogo será accesible en esta dirección
            </label>
            <div
                class="flex items-center font-mono text-lg break-all flex-wrap"
            >
                <span class="text-theme-muted">https://</span>
                <input
                    v-model="slugBase"
                    :disabled="readOnly"
                    class="outline-none bg-transparent text-theme-primary font-mono text-lg min-w-0"
                    :class="readOnly ? '' : 'border-b-2 border-stack-primary'"
                    :style="{ width: slugBaseWidth }"
                    maxlength="80"
                    @input="onSlugBaseInput"
                />
                <span v-if="slugPrefix" class="text-theme-muted"
                    >-{{ slugPrefix }}</span
                >
                <span v-if="slugNumber" class="text-theme-muted"
                    >-{{ slugNumber }}</span
                >
                <span class="text-theme-muted">.{{ $franchiseDomain }}</span>
            </div>
            <p v-if="!readOnly" class="text-xs text-theme-muted mt-2">
                Puedes editar la parte resaltada. Solo letras minúsculas,
                números y guiones.
            </p>
            <p v-if="slugError" class="text-theme-danger text-xs mt-1">
                {{ slugError }}
            </p>
        </div>

        <legal-consent-checkboxes
            v-if="showLegalConsents"
            v-model="legalConsentsAccepted"
            :documents="pendingLegalDocuments"
            class="mt-6 w-full"
        />

        <p v-if="error" class="text-theme-danger text-sm mt-4">
            {{ error }}
        </p>
    </div>
</template>

<script>
import onboardingCompletedGuard from "@/mixins/onboardingCompletedGuard";
import onboardingStepNavigation from "@/mixins/onboardingStepNavigation";
import { mapActions, mapState } from "vuex";

import { toAcceptancePayload } from "@/common/fields/legal-documents";

import LegalConsentCheckboxes from "@/components/legal/LegalConsentCheckboxes.vue";

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export default {
    name: "OnboardingSlugPage",

    components: { LegalConsentCheckboxes },

    mixins: [onboardingCompletedGuard, onboardingStepNavigation],

    data() {
        return {
            stepKey: "slug",
            tenantName: "",
            slugBase: "",
            slugPrefix: "",
            slugNumber: "",
            slugError: "",
            nameTouched: false,
            baseManuallyEdited: false,
            isLoading: false,
            isFetchingSlug: false,
            error: "",
            debounceTimer: null,
            legalConsentsAccepted: false
        };
    },

    computed: {
        // `authTenantName` (no `tenantName`): el input de este step es la
        // fuente de verdad mientras el usuario escribe — un computed mapeado
        // con el mismo nombre que el `data()` de abajo lo tapa en silencio
        // (sin setter), dejando el campo efectivamente de solo lectura. Solo
        // se usa como valor inicial en `mounted` si no hay nada guardado aun.
        ...mapState("auth", {
            authTenantName: "tenantName",
            tenantSlug: "tenantSlug",
            pendingLegalDocuments: "pendingLegalDocuments"
        }),
        showLegalConsents() {
            return !this.readOnly && this.pendingLegalDocuments.length > 0;
        },
        isValidName() {
            const len = this.tenantName.trim().length;
            return len >= 3 && len <= 120;
        },
        isValidSlug() {
            if (!this.slugBase) return false;
            return SLUG_REGEX.test(this.slugBase);
        },
        slugBaseWidth() {
            const len = Math.max(this.slugBase.length, 3);
            return `${len + 1}ch`;
        },
        slugComplete() {
            return (
                this.slugBase +
                (this.slugPrefix ? `-${this.slugPrefix}` : "") +
                (this.slugNumber ? `-${this.slugNumber}` : "")
            );
        },
        localStepperUi() {
            return {
                nextDisabled:
                    !this.isValidName ||
                    !this.isValidSlug ||
                    this.isFetchingSlug ||
                    (this.showLegalConsents && !this.legalConsentsAccepted),
                loading: this.isLoading,
                nextLabel: "",
                nextVariant: "primary"
            };
        }
    },

    async mounted() {
        try {
            const data = await this.getOnboardingStatus();
            const flow = data.onboardingFlow;
            if (flow) {
                if (this.redirectIfOnboardingCompleted(flow)) return;
                if (!this.$route.query.review) {
                    const forwardStep =
                        flow.status === "completed" && flow.nextStep
                            ? flow.nextStep
                            : flow.step;
                    if (forwardStep !== "slug") {
                        const route = this.routeForStep(forwardStep);
                        if (route) {
                            this.$router.push(route);
                            return;
                        }
                    }
                }
            }
        } catch {
            // No status endpoint available
        }

        const savedName = this.selections.name || this.authTenantName;
        const savedSlug = this.selections.slug || this.tenantSlug;
        if (savedName) {
            this.tenantName = savedName;
        }
        if (savedSlug) {
            this.hydrateFromSavedSlug(savedSlug);
        }
        if (this.tenantName && !this.slugBase && !this.readOnly) {
            this.fetchSlugSuggestion();
        }
    },

    beforeDestroy() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    },

    methods: {
        ...mapActions("onboarding", [
            "getOnboardingStatus",
            "getSlugSuggestion",
            "submitSlug"
        ]),
        ...mapActions("legal", [
            "acceptLegalDocuments",
            "loadPendingLegalDocuments"
        ]),

        // Hidratación local de un slug ya asignado (revisión/resubmission):
        // solo separa visualmente base/prefix/number para mostrarlo, no
        // valida disponibilidad (ya es del propio tenant) ni depende del
        // listado de keywords del backend.
        hydrateFromSavedSlug(slug) {
            const parts = slug.split("-");
            if (parts.length < 2) {
                this.slugBase = slug;
                this.slugPrefix = "";
                this.slugNumber = "";
                return;
            }
            const last = parts[parts.length - 1];
            const hasNumber = parts.length > 2 && /^\d+$/.test(last);
            const prefixIndex = hasNumber ? parts.length - 2 : parts.length - 1;
            this.slugNumber = hasNumber ? last : "";
            this.slugPrefix = parts[prefixIndex];
            this.slugBase = parts.slice(0, prefixIndex).join("-");
        },

        onNameInput() {
            if (!this.baseManuallyEdited) {
                this.debounceFetchSlugSuggestion();
            }
        },

        onSlugBaseInput() {
            this.baseManuallyEdited = true;
            this.slugBase = this.slugBase
                .toLowerCase()
                .replace(/[^a-z0-9-]/g, "")
                .replace(/--+/g, "-");
            this.slugError = "";
            if (this.slugBase && !SLUG_REGEX.test(this.slugBase)) {
                this.slugError =
                    "Solo letras minúsculas, números y guiones (sin guiones al inicio o final)";
            }
            this.debounceFetchSlugSuggestion();
        },

        debounceFetchSlugSuggestion() {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }
            this.debounceTimer = setTimeout(() => {
                this.fetchSlugSuggestion();
            }, 300);
        },

        // Le pide al backend la sugerencia dividida en base/prefix/number.
        // Enviar el prefix/number actuales le permite al backend conservarlos
        // si el nuevo candidato sigue disponible, así el usuario no ve el
        // prefix cambiar en cada intento. Si el usuario ya editó la base a
        // mano, se envía explícita para que no vuelva a seguir el nombre.
        async fetchSlugSuggestion() {
            const name = this.tenantName.trim();
            if (name.length < 3 || this.slugError) {
                return;
            }
            const params = { name };
            if (this.baseManuallyEdited && this.slugBase) {
                params.base = this.slugBase;
            }
            if (this.slugPrefix) {
                params.prefix = this.slugPrefix;
                if (this.slugNumber) {
                    params.number = this.slugNumber;
                }
            }
            this.isFetchingSlug = true;
            try {
                const data = await this.getSlugSuggestion(params);
                this.applySuggestion(data);
            } catch {
                // Keep previous values
            } finally {
                this.isFetchingSlug = false;
            }
        },

        applySuggestion({ base, number, prefix }) {
            this.slugBase = base;
            this.slugPrefix = prefix;
            this.slugNumber = number ? String(number) : "";
        },

        stepperNext() {
            if (this.readOnly) {
                this.goToNextReachable();
                return;
            }
            this.continueStep();
        },

        async continueStep() {
            if (this.readOnly) return;
            this.nameTouched = true;
            if (!this.isValidName || !this.isValidSlug || this.isLoading) {
                return;
            }

            if (this.showLegalConsents && !this.legalConsentsAccepted) {
                return;
            }

            this.isLoading = true;
            this.error = "";
            if (this.showLegalConsents) {
                const accepted = await this.acceptPendingLegalDocuments();
                if (!accepted) return;
            }
            try {
                const data = await this.submitSlug({
                    name: this.tenantName.trim(),
                    slug: this.slugComplete
                });
                this.isLoading = false;
                if (data.slugSuggestion) {
                    this.applySuggestion(data.slugSuggestion);
                    this.error =
                        "Justo cuando ibas a guardar, alguien más tomó ese slug. Te asignamos uno nuevo, revísalo y continúa.";
                    return;
                }
                this.navigateByFlow(data.onboardingFlow);
            } catch (err) {
                this.isLoading = false;
                const msg = err.message || "";
                if (msg.includes("not available")) {
                    this.error =
                        "Este slug ya está en uso, prueba editando la parte resaltada";
                } else {
                    this.error = msg || "Error al guardar el nombre";
                }
            }
        },

        async acceptPendingLegalDocuments() {
            try {
                await this.acceptLegalDocuments({
                    documents: toAcceptancePayload(this.pendingLegalDocuments)
                });
                return true;
            } catch (err) {
                this.isLoading = false;
                this.error =
                    err.message ||
                    "No pudimos guardar tu aceptación. Intenta de nuevo.";
                if (err.statusCode === 400) {
                    this.loadPendingLegalDocuments().catch(() => {});
                }
                return false;
            }
        },

        navigateByFlow(flow) {
            if (!flow) return;
            if (flow.status === "completed" && !flow.nextStep) {
                this.$router.push(this.completionRoute);
                return;
            }
            const route = this.routeForStep(flow.step);
            if (route) {
                this.$router.push(route);
            }
        }
    }
};
</script>
